import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser, 
  type User, 
  type CreateUserPayload, 
  type UpdateUserPayload 
} from '../api/users'

interface UsersState {
  users: User[]
  loading: boolean
  error: string | null
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
}

// Thunk to fetch users list
export const fetchUsersList = createAsyncThunk(
  'users/fetchUsersList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUsers()
      return response.data.users
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch users')
    }
  }
)

// Thunk to create a user
export const createUserThunk = createAsyncThunk(
  'users/createUser',
  async (payload: CreateUserPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createUser(payload)
      dispatch(fetchUsersList())
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create user')
    }
  }
)

// Thunk to update a user
export const updateUserThunk = createAsyncThunk(
  'users/updateUser',
  async (payload: UpdateUserPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateUser(payload)
      dispatch(fetchUsersList())
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user')
    }
  }
)

// Thunk to delete/deactivate a user
export const deleteUserThunk = createAsyncThunk(
  'users/deleteUser',
  async (id: number, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteUser(id)
      dispatch(fetchUsersList())
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete user')
    }
  }
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    addUserLocal(state, action: { payload: User }) {
      state.users = [action.payload, ...state.users]
    },
    updateUserLocal(state, action: { payload: User }) {
      state.users = state.users.map((u) => (u.id === action.payload.id ? action.payload : u))
    },
    deleteUserLocal(state, action: { payload: number }) {
      state.users = state.users.filter((u) => u.id !== action.payload)
    },
    clearUsersList(state) {
      state.users = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsersList.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload
      })
      .addCase(fetchUsersList.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { addUserLocal, updateUserLocal, deleteUserLocal, clearUsersList } = usersSlice.actions
export default usersSlice.reducer
